'use client';

import { c as _c } from "react/compiler-runtime";
import { FC, lazy, memo, ReactNode, useLayoutEffect, useMemo, useRef } from 'react';
import PostHero from '@/components/BlogPage/PostHero';
import { BlogPostDto } from '@/components/BlogPage/types';
import MediaInfoGrid from '@/components/CaseStudy/MediaInfoGrid';
import LazySection from '@/components/LazySection';
import cn from '@/helpers/cn';
import slugify from '@/helpers/slugify';
import { gsap, ScrollTrigger } from '@/lib/gsap';
import PersonCard from './PersonCard';
import PostCredits from './PostCredits';
import css from './style.module.scss';
import TableOfContents from './TableOfContents';
const MediaOnlyBlock = lazy(() => import('@/components/CaseStudy/MediaOnlyBlock'));
const MarkdownBlock = lazy(() => import('@/components/BlogPage/MarkdownBlock'));
const CtaBlock = lazy(() => import('@/components/BlogPage/CtaBlock'));
const CaseStudyLinkBlock = lazy(() => import('@/components/BlogPage/CaseStudyLinkBlock'));
const ShareBlock = lazy(() => import('@/components/BlogPage/ShareBlock'));
const SummaryBlock = lazy(() => import('@/components/BlogPage/BlogPost/SummaryBlock'));
const SectionCaseStudy = lazy(() => import('@/components/BlogPage/SectionCaseStudy'));
const TableBlock = lazy(() => import('@/components/BlogPage/TableBlock'));
const Faq = lazy(() => import('@/components/service-pages/Faq'));
const components = {
  'case.media-block': memo(MediaOnlyBlock),
  'blog.markdown-block': memo(MarkdownBlock),
  'blog.cta-block': memo(CtaBlock),
  'blog.case-study-link': memo(CaseStudyLinkBlock),
  'blog.share-block': memo(ShareBlock),
  'blog.summary-block': memo(SummaryBlock),
  'case.media-info-grid': memo(MediaInfoGrid),
  'blog.section-case-study': memo(SectionCaseStudy),
  'blog.table': memo(TableBlock),
  'website-prod-page.faq-section': memo(Faq)
};
interface Props {
  data: BlogPostDto;
  relevantPosts?: ReactNode;
}
const extractHeadings = (content: any[]): {
  id: string;
  text: string;
}[] => {
  const headings: {
    id: string;
    text: string;
  }[] = [];
  content.forEach(block => {
    if (block.__component === 'blog.markdown-block' && Array.isArray(block.text)) block.text.forEach((node: any) => {
      if (node.type === 'heading' && node.level === 2) {
        const text = node.children?.map((c: any) => c.text ?? '').join('') ?? '';
        if (text) headings.push({
          id: slugify(text),
          text
        });
      }
    });
  });
  return headings;
};
const BlogPost: FC<Props> = t0 => {
  const $ = _c(37);
  const {
    data,
    relevantPosts
  } = t0;
  const mainWrapperRef = useRef(null);
  const tocRef = useRef(null);
  const sidebarRef = useRef(null);
  let t1;
  if ($[0] !== data.content) {
    t1 = data?.content ? extractHeadings(data.content as any[]) : [];
    $[0] = data.content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const headings = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();
        mm.add("(min-width: 992px)", () => {
          if (!mainWrapperRef.current) {
            return;
          }
          if (tocRef.current) {
            ScrollTrigger.create({
              trigger: mainWrapperRef.current,
              start: "top top",
              end: "bottom bottom",
              pin: tocRef.current
            });
          }
          if (sidebarRef.current) {
            ScrollTrigger.create({
              trigger: mainWrapperRef.current,
              start: "top top",
              end: "bottom bottom",
              pin: sidebarRef.current
            });
          }
        });
      });
      return () => ctx.revert();
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn("container", css.container);
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== data) {
    t5 = <PostHero post={data} />;
    $[5] = data;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== headings) {
    t6 = <div ref={tocRef} className={css.tocWrapper}><TableOfContents headings={headings} /></div>;
    $[7] = headings;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== data.content) {
    t7 = data?.content?.length ? (data.content as any[]).map(_temp) : null;
    $[9] = data.content;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== data) {
    t8 = <PostCredits post={data} />;
    $[11] = data;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t7 || $[14] !== t8) {
    t9 = <article className={css.content}>{t7}{t8}</article>;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== data) {
    t10 = data.author ? <PersonCard person={data.author} currentPost={data} label="Author" /> : null;
    $[16] = data;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== data.author || $[19] !== data.expert) {
    t11 = data.author && data.expert ? <div className={css.sidebarSeparator} /> : null;
    $[18] = data.author;
    $[19] = data.expert;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== data) {
    t12 = data.expert ? <PersonCard person={data.expert} currentPost={data} label="Expert" /> : null;
    $[21] = data;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12) {
    t13 = <div ref={sidebarRef} className={css.sidebarWrapper}>{t10}{t11}{t12}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t6 || $[29] !== t9) {
    t14 = <div ref={mainWrapperRef} className={css.mainWrapper}>{t6}{t9}{t13}</div>;
    $[27] = t13;
    $[28] = t6;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== relevantPosts) {
    t15 = <LazySection>{relevantPosts}</LazySection>;
    $[31] = relevantPosts;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15 || $[35] !== t5) {
    t16 = <div className={t4}>{t5}{t14}{t15}</div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t5;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
};
export default BlogPost;
function _temp(block, index) {
  const Component = components[block.__component as keyof typeof components];
  return Component ? <Component key={`block_${index}`} data={block} /> : null;
}